import Dotdotdot from 'react-dotdotdot';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { FakeLink } from '@hivebrite/design-system/Link';
import { primaryShadow, useBreakpoint } from '@hivebrite/design-system/Theme';

import { Link } from 'hb-react/frontoffice/components/Link';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import useFrameTheme from 'hb-react/shared/hooks/theme/useFrameTheme';
import useHref from 'hb-react/shared/hooks/useHref';
import useImmersiveMenu from 'hb-react/shared/hooks/useImmersiveMenu';
import ThemeProvider from 'hb-react/style/ThemeProvider';

interface GroupThumbnailProps {
  cover_picture_medium_url: string;
  cover_picture_small_url: string;
  landing_page: string;
  name: string;
  white_label_url: string | null;
}

export const GroupThumbnail = ({
  cover_picture_medium_url: coverPictureMediumUrl,
  cover_picture_small_url: coverPictureSmallUrl,
  landing_page: landingPage,
  name,
  white_label_url: whiteLabelURL,
}: GroupThumbnailProps) => {
  const breakpoint = useBreakpoint();
  const { currentNetwork } = useDefaultAppContexts();
  const frameTheme = useFrameTheme();

  const coverPicture =
    coverPictureSmallUrl === '/images/missing/topics/cover_picture/default.jpg'
      ? coverPictureMediumUrl
      : coverPictureSmallUrl;

  const isImmersiveMenu = useImmersiveMenu({
    location: { pathname: landingPage },
    network: currentNetwork,
  });

  const href = useHref(whiteLabelURL, landingPage);
  const forceDefaultLink = !!(whiteLabelURL || isImmersiveMenu);

  return (
    <Link to={href} tabIndex={0} forceDefaultLink={forceDefaultLink}>
      <Card bg={frameTheme.backgroundColor}>
        <ThemeProvider
          theme={frameTheme.themeName}
          fontBrightness={frameTheme.fontBrightness}
        >
          <Cover $backgroundImage={coverPicture} />
          <Name $hideLinkEffect={breakpoint === 's'}>
            <Clamped>{name}</Clamped>
          </Name>
        </ThemeProvider>
      </Card>
    </Link>
  );
};

const Card = styled(Box).attrs(() => ({
  flexDirection: 'column',
  height: '100%',
}))`
  border-radius: ${({ theme }) =>
    `${theme.themeVariables.globalBorderRadius}px`};
  overflow: hidden;
  ${primaryShadow};
`;

const Cover = styled(Box).attrs(() => ({
  width: '100%',
}))<{ $backgroundImage: string }>`
  aspect-ratio: 757 / 418;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  background-image: url(${({ $backgroundImage }) => $backgroundImage});
  border-top-left-radius: ${({ theme }) =>
    `${theme.themeVariables.globalBorderRadius}px`};
  border-top-right-radius: ${({ theme }) =>
    `${theme.themeVariables.globalBorderRadius}px`};
`;

const hideLinkEffectStyle = css`
  &:hover {
    color: ${({ theme }) => theme.colors.black};
    text-decoration: none;
  }
`;

const Name = styled(FakeLink).attrs(() => ({
  styledOnlyOnHover: true,
  font: 'h4-semibold',
  p: 3,
  tabIndex: -1,
}))<{ $hideLinkEffect: boolean }>`
  ${({ $hideLinkEffect }) => ($hideLinkEffect ? hideLinkEffectStyle : '')}
`;

const Clamped = styled(Dotdotdot).attrs(() => ({
  clamp: 2,
  useNativeClamp: true,
}))`
  padding: 2px;
  margin: -2px;
`;
